import { Fragment } from "react";
import { Check, Minus } from "lucide-react";
import { TIER_ENTITLEMENTS, tierDisplayName, describeLibraryItemsCap, type AiBundle } from "@/lib/tier";
import { cn } from "@/lib/utils";
import type { PaidTier } from "./pricingConstants";

const COMPARE_TIERS: PaidTier[] = ["starter", "plus", "pro", "elite", "agency"];

const fmtNum = (n: number | null, unlimitedLabel = "Unlimited") =>
  n === null ? unlimitedLabel : n.toLocaleString();

const fmtAddons = (n: number | null) =>
  n === null ? "Unlimited" : n === 0 ? "—" : `Up to ${n}`;

const MB = 1024 * 1024;
const GB = 1024 * MB;
const fmtBytes = (n: number | null) => {
  if (n === null) return "Unlimited";
  if (n >= GB) return `${(n / GB).toFixed(n % GB === 0 ? 0 : 1)} GB`;
  return `${Math.round(n / MB)} MB`;
};

const AI_BUNDLE_LABEL: Record<AiBundle, { primary: string; secondary?: string }> = {
  none: { primary: "—" },
  lovable_basic_metered: { primary: "Excerptus AI", secondary: "+ BYOK (Optional)" },
  lovable_basic: { primary: "Excerptus AI", secondary: "+ BYOK (Optional)" },
  lovable_plus_premium_capped: { primary: "Excerptus AI", secondary: "+ BYOK (Optional)" },
  lovable_plus_premium: { primary: "Excerptus AI", secondary: "+ BYOK (Optional)" },
  byok_required: { primary: "BYOK required" },
  lovable_plus_premium_byok: { primary: "Excerptus AI", secondary: "+ BYOK" },
};

type CellValue = string | boolean | { primary: string; secondary?: string };
type Row = { label: string; sublabel?: string; values: CellValue[] };
type Group = { title: string; rows: Row[] };

const buildGroups = (): Group[] => {
  const e = COMPARE_TIERS.map((t) => TIER_ENTITLEMENTS[t]);
  const map = <T,>(fn: (x: typeof e[number]) => T) => e.map(fn);

  // Per-workspace × effective workspace count helper for the MCP Outbound row.
  const mcpOutboundCell = (tier: PaidTier): string | boolean => {
    const ent = TIER_ENTITLEMENTS[tier];
    if (!ent.capabilities.mcp_outbound || ent.mcpOutboundPerWorkspace === 0) return false;
    const per = ent.mcpOutboundPerWorkspace;
    const base = ent.workspaces ?? 0;
    const addOnsUnlimited = ent.workspaceAddOnMax === null;
    const total = (base + (ent.workspaceAddOnMax ?? 0)) * per;
    if (addOnsUnlimited) return `${per} / workspace (100+ with add-ons)`;
    if ((ent.workspaceAddOnMax ?? 0) > 0) return `${per} / workspace (${total}+ with add-ons)`;
    return `${per} / workspace (≈${total} total)`;
  };

  return [
    {
      title: "Capacity",
      rows: [
        { label: "Workspaces (base)", values: map((x) => fmtNum(x.workspaces)) },
        { label: "Workspace add-ons", values: map((x) => fmtAddons(x.workspaceAddOnMax)) },
        { label: "My Library sources", values: COMPARE_TIERS.map((t) => describeLibraryItemsCap(t)) },
        { label: "Excerpts", values: map((x) => fmtNum(x.excerpts)) },
        { label: "Publishing connectors", values: map((x) => fmtNum(x.publishingConnectors)) },
        { label: "Readwise sync", values: map(() => true) },
        { label: "RSS Blog Feed subscriptions / workspace", values: map((x) => fmtNum(x.feedSubscriptionsMax)) },
        { label: "Podcast Feed subscriptions / workspace", values: map((x) => fmtNum(x.podcastSubscriptionsMax)) },
        { label: "My Corpus documents / workspace", values: map((x) => fmtNum(x.corpusDocumentCap)) },
        { label: "Workspace media storage", values: map((x) => fmtBytes(x.workspaceMediaBytes)) },
      ],
    },
    {
      title: "AI",
      rows: [
        { label: "AI Bundle", values: map((x) => AI_BUNDLE_LABEL[x.aiBundle]) },
        {
          label: "Monthly Excerptus Credits",
          values: map((x) => (x.monthlyAiCredits === null ? "BYOK" : x.monthlyAiCredits.toLocaleString())),
        },
        { label: "Excerptus Top-Up Credits Available", values: map(() => true) },
      ],
    },
    {
      title: "Composer",
      rows: [
        { label: "Builder", sublabel: "Blocks & Blueprints", values: map(() => true) },
        { label: "Vault", sublabel: "Templates, Prompts, Archives", values: map(() => true) },
        { label: "Corpus", sublabel: "Primary, Secondary, Supplemental", values: map(() => true) },
      ],
    },
    {
      title: "Knowledge Worker Codex (optional)",
      rows: [
        { label: "Topic Tree Framework", sublabel: "1-5 Columns with 1-8 Topics ea.", values: map(() => true) },
        { label: "Master Voice", sublabel: "Single/Dual Tone, cadence, lexicon", values: map(() => true) },
        { label: "Avatars", sublabel: "Audience Personas", values: map(() => true) },
      ],
    },
    {
      title: "Excerpt Quote Image Generator",
      rows: [
        { label: "Downloads / month", values: map((x) => fmtNum(x.downloadsPerMonth)) },
        { label: "Custom + brand colors", values: map(() => true) },
        { label: "Gradients & overlays", values: map(() => true) },
        { label: "Saved gradient presets", values: map(() => true) },
        { label: "Brand color library", values: map(() => true) },
      ],
    },
    {
      title: "Capabilities",
      rows: [
        {
          label: "MCP Inbound",
          sublabel: "Excerptus MCP server",
          values: map((x) => x.capabilities.mcp_inbound),
        },
        {
          label: "MCP Outbound",
          sublabel: "Connect to external MCP servers",
          values: COMPARE_TIERS.map((t) => mcpOutboundCell(t)),
        },
        { label: "Multi-workspace coordination", values: map((x) => x.capabilities.content_multi_workspace) },
        {
          label: "Per-workspace library isolation",
          // Elite = opt-in, Agency = on by default; both shown as included.
          values: COMPARE_TIERS.map((t) => t === "elite" || t === "agency"),
        },
        { label: "Affiliate Program", values: map(() => true) },
      ],
    },
  ];
};


const Cell = ({ value }: { value: CellValue }) => {
  if (typeof value === "boolean") {
    return value ? (
      <Check className="mx-auto h-4 w-4 text-primary" aria-label="Included" />
    ) : (
      <Minus className="mx-auto h-4 w-4 text-muted-foreground/60" aria-label="Not included" />
    );
  }
  if (typeof value === "object") {
    return (
      <span className="text-sm">
        <span className="block">{value.primary}</span>
        {value.secondary && (
          <span className="block text-xs text-muted-foreground">{value.secondary}</span>
        )}
      </span>
    );
  }
  return <span className="text-sm">{value}</span>;
};


export const PlanComparisonGrid = () => {
  const groups = buildGroups();

  return (
    <div className="mt-8">
      {/* Desktop / tablet table */}
      <div className="hidden lg:block overflow-hidden rounded-xl border border-border bg-card shadow-soft">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col className="w-[20%]" />
            <col className="w-[16%]" />
            <col className="w-[16%]" />
            <col className="w-[16%]" />
            <col className="w-[16%]" />
            <col className="w-[16%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="px-4 py-3 text-sm font-medium text-muted-foreground">Feature</th>
              {COMPARE_TIERS.map((t) => (
                <th
                  key={t}
                  className={cn(
                    "px-4 py-3 text-center font-serif text-base font-semibold",
                    t === "pro" && "text-primary",
                  )}
                >
                  {tierDisplayName(t)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.title}>
                <tr className="bg-muted/20">
                  <th
                    colSpan={1 + COMPARE_TIERS.length}
                    className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {g.title}
                  </th>
                </tr>
                {g.rows.map((r) => (
                  <tr key={r.label} className="border-t border-border/60">
                    <td className="px-4 py-3 text-sm text-foreground align-top whitespace-normal break-words">
                      <div>{r.label}</div>
                      {r.sublabel && (
                        <div className="mt-0.5 text-xs text-muted-foreground">{r.sublabel}</div>
                      )}
                    </td>
                    {r.values.map((v, i) => (
                      <td
                        key={COMPARE_TIERS[i]}
                        className="px-3 py-3 text-center align-top whitespace-normal break-words"
                      >
                        <Cell value={v} />
                      </td>
                    ))}
                  </tr>
                ))}

              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile / small-tablet stacked panels */}
      <div className="grid gap-4 lg:hidden sm:grid-cols-2">
        {COMPARE_TIERS.map((t, tierIdx) => (
          <div
            key={t}
            className={cn(
              "rounded-xl border bg-card p-4 shadow-soft",
              t === "pro" ? "border-primary/60" : "border-border",
            )}
          >
            <h3 className="font-serif text-lg font-semibold">{tierDisplayName(t)}</h3>
            <div className="mt-3 space-y-4">
              {groups.map((g) => (
                <div key={g.title}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.title}
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {g.rows.map((r) => {
                      const v = r.values[tierIdx];
                      return (
                        <li key={r.label} className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-muted-foreground">
                            <span className="block">{r.label}</span>
                            {r.sublabel && (
                              <span className="block text-xs text-muted-foreground/80">{r.sublabel}</span>
                            )}
                          </span>
                          <span className="text-right">
                            {typeof v === "boolean" ? (
                              v ? (
                                <Check className="h-4 w-4 text-primary" aria-label="Included" />
                              ) : (
                                <Minus className="h-4 w-4 text-muted-foreground/60" aria-label="Not included" />
                              )
                            ) : typeof v === "object" ? (
                              <span>
                                <span className="block">{v.primary}</span>
                                {v.secondary && (
                                  <span className="block text-xs text-muted-foreground/80">{v.secondary}</span>
                                )}
                              </span>
                            ) : (
                              v
                            )}
                          </span>
                        </li>

                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};
